
import {
  computed,
  defineComponent, onMounted, Ref, ref, ComputedRef, reactive, onUnmounted, watch,
} from 'vue';

import { useRouter } from 'vue-router';
import { Form, defineRule } from 'vee-validate';
import AppHeader from '@/components/AppHeader.vue';
import AppSearch from '@/components/UI/AppSearch.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import {
  IConversation, ICreateGroup, IFileUpload, IImageCrop, IPhoto, ISpacesContact,
} from '@/types/interfaces';
import AppInput from '@/components/UI/AppInput.vue';
import AppToggle from '@/components/UI/AppToggle.vue';
import SpacesService from '@/modules/spaces/services/SpacesService';
import AppContactItem from '@/components/AppContactItem.vue';
import { useMainStore } from '@/store/mainStore';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import {
  CONTACT_STATUS, GROUP_TYPE, NETWORK_CONTACT_TYPES, PHOTO_VIEW_TYPE, UPLOAD_PLACES,
} from '@/types/enums';
import ValidationService from '@/services/ValidationService';
import AppLoader from '@/components/UI/AppLoader.vue';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import AppUploadInfo from '@/components/UI/AppUploadInfo.vue';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import AppSkeletonDialog from '@/components/skeletons/AppSkeletonDialog.vue';
import { useMessagesStore } from '../store/messagesStore';
import WebsocketService from '@/services/WebsocketService';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';

defineRule('required', (value: string) => ValidationService.isNotEmpty(value) || 'This field is required');
defineRule('min5', (value: string) => ValidationService.isLonger(value, 5) || 'Group name must be at least 5 characters long.');

export default defineComponent({
  emits: ['close'],
  components: {
    AppHeader,
    AppSearch,
    AppInput,
    AppToggle,
    AppContactItem,
    AppTakePhoto,
    AppLoader,
    AppUploadInfo,
    Form,
    AppSkeletonDialog,
    AppItemWrapper,
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const messagesStore = useMessagesStore();
    const spacesStore = useSpacesStore();
    const router = useRouter();

    const schema = {
      groupName: 'required|min5',
    };

    const isValid: Ref<boolean> = ref(false);

    const contacts: Ref<ISpacesContact[]> = ref([]);
    const invitedUserIds = ref<string[]>([]);
    const isGroupPublic = ref(false);

    const filteredContacts: ComputedRef<ISpacesContact[]> = computed(
      () => contacts.value.filter((cont) => {
        const filteredSearch = `${cont.firstName} ${cont.lastName}`.toLowerCase().includes(driveStore.search.toLowerCase());
        return filteredSearch && !cont.defaultUser;
      }),
    );
    const isUploading: Ref<boolean> = ref(false);
    const loader: Ref<string> = ref('');

    const errorMessage = ref('');

    const { onUploadUpdate, uploadInfo } = UseUpload();

    const loaderText = computed(() => {
      if (loader.value) {
        return loader.value;
      }
      // eslint-disable-next-line max-len
      return '';
    });

    const registrationGroupData = reactive({
      name: '',
      ownerId: mainStore.user.accountId,
      photo: null,
      get groupType() {
        return isGroupPublic.value ? GROUP_TYPE.PUBLIC_OPEN : GROUP_TYPE.PRIVATE;
      },
    } as ICreateGroup);

    const fileInput: Ref<HTMLInputElement | null> = ref(null);
    const fileInputKey = ref(0);
    const imageToCrop = ref({} as IImageCrop);

    const imageToUpload = ref();

    const photoUrl = computed(() => {
      if (imageToUpload.value) {
        return registrationGroupData.photo!.url;
      }
      if (registrationGroupData.photo) {
        return lowResUrlByViewTypeByUrl(registrationGroupData.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/Group.svg');
    });

    function savePhoto(event: Event) {
      const img: File = (event.target as HTMLInputElement).files![0];

      imageToCrop.value = {
        url: URL.createObjectURL(img),
        fileName: img.name,
        fileType: img.type,
      };
      fileInputKey.value += 1;

      fileInput.value!.value = '';
    }

    function bufferCropImage(fileUpload: { file: IFileUpload[] }) {
      imageToUpload.value = fileUpload;

      const photo: IPhoto = {
        id: Date.now().toString(),
        url: URL.createObjectURL(fileUpload.file[0].file),
      };

      registrationGroupData.photo = photo;
    }

    async function handleCroppedImage(fileUpload: { file: IFileUpload[] }) {
      // isUploading.value = true;

      return UploadService.batchUploadTUS(fileUpload.file, onUploadUpdate, { location: UPLOAD_PLACES.PROFILE_PHOTO }, undefined, [], true, true)
        .then((response) => {
          const res = response[0];
          const photo: IPhoto = {
            id: res.id,
            url: res.fileUrl,
          };

          registrationGroupData.photo = photo;

          return response;
        });
      // .finally(() => { isUploading.value = false; });
    }

    function validate() {
      isValid.value = ValidationService.isNotEmpty(registrationGroupData.name) && ValidationService.isLonger(registrationGroupData.name, 5);
      if (isValid.value) {
        errorMessage.value = '';
      } else if (!ValidationService.isNotEmpty(registrationGroupData.name)) {
        errorMessage.value = 'This field is required';
      } else if (!ValidationService.isLonger(registrationGroupData.name, 5)) {
        errorMessage.value = 'Group name must be at least 5 characters long.';
      }
    }

    function createGroup() {
      registrationGroupData.name = registrationGroupData.name.trim();
      validate();

      if (!isValid.value) {
        return;
      }
      messagesStore.currentConversation = { info: { name: registrationGroupData.name } } as IConversation;
      loader.value = 'Creating group';

      SpacesService.createGroup({ ...registrationGroupData, photo: null }).then((res) => {
        if (res.success) {
          const payload = invitedUserIds.value.map((accountId) => ({
            initiatorId: mainStore.user.accountId,
            targetId: accountId,
            groupId: res.results.data.id,
          }));

          if (imageToUpload.value) {
            handleCroppedImage(imageToUpload.value).then((media) => {
              const photo = {
                id: media[0].id,
                url: media[0].fileUrl,
              };

              const updatePayload = {
                id: res.results.data.id,
                photo,
                name: registrationGroupData.name,
              };

              if (messagesStore.currentConversation?.info) {
                messagesStore.currentConversation!.info.photo = photo;
              }

              SpacesService.updateGroup(updatePayload);
            });
          }

          // WebsocketService.sendMessage(res.results.data.id, `Group ${res.results.data.name} was created`, true);

          SpacesService.inviteUsersToGroup(payload).then(() => {
            emit('close');
            router.push(`/messages/group/${res.results.data.id}?hideSkeletons=true`);
          });

          spacesStore.myGroups.unshift(res.results.data);
        } else {
          errorMessage.value = res.errorKey as string;
        }
      }).finally(() => { setTimeout(() => { loader.value = ''; }, 1000); });
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      isUploading.value = false;
    }

    function inviteToGroup(contact: ISpacesContact) {
      invitedUserIds.value.push(contact.accountId);
    }

    function resolveUserType(contactStatus: CONTACT_STATUS, accountId: string): NETWORK_CONTACT_TYPES {
      if (contactStatus === CONTACT_STATUS.REQUEST_SENT) {
        return NETWORK_CONTACT_TYPES.OUTCOMING;
      }
      if (contactStatus === CONTACT_STATUS.REQUEST_RECEIVED) {
        return NETWORK_CONTACT_TYPES.INVITATION;
      }
      if (contactStatus === CONTACT_STATUS.CONTACT) {
        return NETWORK_CONTACT_TYPES.MY;
      }
      if (accountId === mainStore.user.accountId) {
        return NETWORK_CONTACT_TYPES.ME;
      }
      return NETWORK_CONTACT_TYPES.SUGGESTION;
    }

    const myContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => {
        const type = resolveUserType(cont.contactStatus, cont.accountId);
        return type === NETWORK_CONTACT_TYPES.MY && !cont.defaultUser;
      })];
      return arr;
    });

    const notMyContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => {
        const type = resolveUserType(cont.contactStatus, cont.accountId);
        return type !== NETWORK_CONTACT_TYPES.MY && type !== NETWORK_CONTACT_TYPES.ME && !cont.defaultUser;
      })];
      return arr;
    });

    // search logic
    const scroller = ref<HTMLElement | null>(null);

    let pagesAmount = 0;
    let currentPageIndex = 0;

    function searchContacts(query: string, pageIndex: number, pageSize: number, push?: boolean): void {
      spacesStore.searchContacts(query, pageIndex, pageSize, push).then((response) => {
        if (response.success) {
          pagesAmount = response.results.data.totalPages;
          currentPageIndex = response.results.data.number;
        }
      });
    }

    function onScroll() {
      const isBottom = Math.round(scroller.value!.scrollTop + scroller.value!.clientHeight) >= Math.round(scroller.value!.scrollHeight);

      if (isBottom && pagesAmount !== currentPageIndex + 1) {
        searchContacts(spacesStore.search, currentPageIndex + 1, 10, true);
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      if (newSearch === '') {
        spacesStore.searchContactsList = [];
      } else {
        timeout = setTimeout(() => {
          searchContacts(newSearch, 0, 10);
        }, 300);
      }
    });

    onMounted(() => {
      SpacesService.fetchMyContacts().then((response) => {
        if (response.success) {
          contacts.value = response.results.data.map((cont) => cont.user);
        }
      });
    });

    onUnmounted(() => {
      driveStore.search = '';
    });

    return {
      driveStore,
      photoUrl,
      fileInputKey,
      imageToCrop,
      contacts,
      invitedUserIds,
      registrationGroupData,
      filteredContacts,
      schema,
      isValid,
      loader,
      isUploading,
      loaderText,
      uploadInfo,
      fileInput,
      errorMessage,
      cancelUpload,
      validate,
      savePhoto,
      createGroup,
      handleCroppedImage,
      bufferCropImage,
      inviteToGroup,
      mainStore,
      myContactsSearch,
      notMyContactsSearch,
      onScroll,
      scroller,
      isGroupPublic,
    };
  },
});
